import {
  Fragment,
  useId,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { Button, Drawer, Typography, makeStyles } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import type { Entity } from '@backstage/catalog-model';
import { Progress } from '@backstage/core-components';
import { useApi, useApiHolder } from '@backstage/core-plugin-api';
import { EntityDisplayName } from '@backstage/plugin-catalog-react';
import {
  scaffolderApiRef,
  useTemplateSecrets,
  type FieldExtensionOptions,
  type FormProps,
  type LayoutOptions,
  type TemplateParameterSchema,
} from '@backstage/plugin-scaffolder-react';
import {
  Form,
  createAsyncValidators,
  useFilteredSchemaProperties,
  useFormDataFromQuery,
  useTemplateParameterSchema,
  useTemplateSchema,
  type FormValidation,
  type ParsedTemplateSchema,
} from '@backstage/plugin-scaffolder-react/alpha';
import type { ErrorSchema } from '@rjsf/utils';
import { customizeValidator } from '@rjsf/validator-ajv8';
import ajvErrors from 'ajv-errors';
import { OutlinedInput, panelFields } from './fields';
import { PanelShell, StepIndicator, usePanelStyles } from './panelParts';
import { RunPhase } from './RunPhase';
import { repoLocationOf, reviewRows, stepNamesOf, titleOf } from './templates';

/**
 * The panel a template's form opens in (DX-650; promised in
 * specs/create-from-a-template.md): one page of the template's form per step,
 * then a review, then the run — all beside the list of templates, as a request
 * is on Access.
 *
 * The form is the template engine's own: the same form component, the same
 * validator (with `errorMessage` support), the same field extensions and their
 * validations, so a template behaves here as it would on the engine's page.
 * What is the portal's own is the frame around it, two fields drawn the way the
 * portal's forms are (`fields.tsx`), the review and the run.
 */

const validator = customizeValidator();
ajvErrors(validator.ajv);

type Values = Record<string, any>;

type Props = {
  templateRef: string;
  /** The template's catalog entry, for its title and the steps a run takes. */
  template?: Entity;
  extensions: FieldExtensionOptions[];
  layouts?: LayoutOptions[];
  formProps?: FormProps;
  onClose: () => void;
  /** Told once a run this panel started has finished, either way. */
  onRunFinished?: () => void;
};

type Phase =
  | { kind: 'form'; page: number }
  | { kind: 'review' }
  | { kind: 'run'; taskId: string };

type FlowProps = Props & {
  manifest: TemplateParameterSchema;
  title: string;
  titleId: string;
};

/** Whether an async validation found anything, at any depth. */
const hasErrors = (errors?: FormValidation): boolean =>
  Object.values(errors ?? {}).some(error => {
    if (!error || typeof error !== 'object') return false;
    if ('__errors' in error) {
      return ((error as { __errors?: string[] }).__errors ?? []).length > 0;
    }
    return hasErrors(error as FormValidation);
  });

type FormPhaseProps = FlowProps & {
  step: ParsedTemplateSchema;
  page: number;
  labels: string[];
  values: Values;
  setValues: Dispatch<SetStateAction<Values>>;
  backLabel: string;
  nextLabel: string;
  onBack?: () => void;
  onNext: () => void;
};

const FormPhase = ({
  step,
  page,
  labels,
  values,
  setValues,
  extensions,
  layouts = [],
  formProps,
  title,
  titleId,
  backLabel,
  nextLabel,
  onClose,
  onBack,
  onNext,
}: FormPhaseProps) => {
  const formId = useId();
  const apiHolder = useApiHolder();
  const fields = useMemo(() => panelFields(extensions), [extensions]);
  const validators = useMemo(
    () =>
      Object.fromEntries(
        extensions.map(({ name, validation }) => [name, validation]),
      ),
    [extensions],
  );
  const validate = useMemo(
    () => createAsyncValidators(step.mergedSchema, validators, { apiHolder }),
    [step, validators, apiHolder],
  );
  const [errors, setErrors] = useState<FormValidation>();
  const [validating, setValidating] = useState(false);

  const {
    formContext,
    uiSchema: sharedUiSchema,
    liveOmit: _liveOmit,
    omitExtraData: _omitExtraData,
    ...restFormProps
  } = formProps ?? {};
  const uiSchema = withLayout({ ...sharedUiSchema, ...step.uiSchema }, layouts);

  const submit = async ({ formData = {} }: { formData?: Values }) => {
    setErrors(undefined);
    setValidating(true);
    let found: FormValidation;
    try {
      found = await validate(formData);
    } finally {
      // A schema the validator cannot read throws; the buttons must not stay
      // disabled for good.
      setValidating(false);
    }
    setValues(current => ({ ...current, ...formData }));
    if (hasErrors(found)) {
      setErrors(found);
    } else {
      onNext();
    }
  };

  return (
    <PanelShell
      titleId={titleId}
      title={title}
      onClose={onClose}
      status={<StepIndicator labels={labels} current={page} />}
      foot={
        <>
          <Button
            variant="outlined"
            onClick={onBack ?? onClose}
            disabled={validating}
          >
            {onBack ? backLabel : 'Cancel'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            form={formId}
            disabled={validating}
          >
            {nextLabel}
          </Button>
        </>
      }
    >
      <Form
        id={formId}
        validator={validator}
        schema={step.schema}
        uiSchema={uiSchema}
        formData={values}
        formContext={{ ...formContext, formData: values }}
        fields={fields}
        templates={{ BaseInputTemplate: OutlinedInput }}
        // What the field extensions' own validations found: the same shape the
        // form's errors have, typed apart by the engine.
        extraErrors={errors as ErrorSchema | undefined}
        showErrorList={false}
        noHtml5Validate
        onChange={event =>
          setValues(current => ({ ...current, ...event.formData }))
        }
        onSubmit={submit}
        experimental_defaultFormStateBehavior={{ allOf: 'populateDefaults' }}
        {...restFormProps}
      >
        {/* The panel's foot submits the form; it draws no button of its own. */}
        <Fragment />
      </Form>
    </PanelShell>
  );
};

/** A layout named by a page of the form, resolved as the engine resolves it. */
function withLayout(uiSchema: Values, layouts: LayoutOptions[]): Values {
  const name = uiSchema['ui:ObjectFieldTemplate'];
  const layout =
    typeof name === 'string'
      ? layouts.find(candidate => candidate.name === name)?.component
      : undefined;
  return layout ? { ...uiSchema, 'ui:ObjectFieldTemplate': layout } : uiSchema;
}

/**
 * What a run is about, for the panel to name it by: the repository, where the
 * form picks one, else the `name` the form asks for.
 */
function subjectOf(
  steps: ParsedTemplateSchema[],
  values: Values,
): string | undefined {
  for (const step of steps) {
    for (const [key, ui] of Object.entries(step.uiSchema ?? {})) {
      const value = values[key];
      if (ui?.['ui:field'] === 'RepoUrlPicker' && typeof value === 'string') {
        const { owner, repo } = repoLocationOf(value);
        if (owner && repo) return `${owner}/${repo}`;
      }
    }
  }
  return typeof values.name === 'string' && values.name
    ? values.name
    : undefined;
}

const useReviewStyles = makeStyles(theme => ({
  values: {
    margin: 0,
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 8rem) minmax(0, 1fr)',
    gap: theme.spacing(1.75, 2),
    fontSize: '0.9375rem',
    '& dt': { color: 'var(--ui-text-muted)' },
    '& dd': { margin: 0, overflowWrap: 'anywhere' },
  },
  stack: { display: 'flex', flexDirection: 'column' },
  mono: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.8125rem',
    color: 'var(--ui-text-muted)',
  },
  code: { fontFamily: 'var(--font-mono)', fontSize: '0.875rem' },
  rule: { height: 1, background: 'var(--ui-border)', border: 0, margin: 0 },
  heading: { margin: 0, fontSize: '0.9375rem', fontWeight: 600 },
  happens: {
    listStyle: 'none',
    margin: theme.spacing(1.5, 0, 0),
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    '& li': { display: 'flex', alignItems: 'center', gap: theme.spacing(1.5) },
  },
  number: {
    flex: 'none',
    display: 'grid',
    placeItems: 'center',
    width: 24,
    height: 24,
    boxSizing: 'border-box',
    borderRadius: '50%',
    border: '1px solid var(--color-brand-300)',
    color: 'var(--color-brand-700)',
    fontSize: '0.8125rem',
    fontWeight: 700,
  },
}));

const ENTITY_REF = /^[a-z][a-z0-9-]*:[^\s/]+\/\S+$/i;

type UiOptions = Record<string, any>;

/** One value of the form, as the reader would recognise it. */
const ReviewValue = ({
  value,
  ui,
  definition,
}: {
  value: unknown;
  ui: UiOptions;
  definition: UiOptions;
}) => {
  const classes = useReviewStyles();
  const mask = ui['ui:backstage']?.review?.mask;
  if (value === undefined || value === '' || value === null) {
    return <span className={classes.mono}>—</span>;
  }
  if (typeof mask === 'string') return <>{mask}</>;
  if (
    String(ui['ui:field']).toLowerCase() === 'secret' ||
    ui['ui:widget'] === 'password'
  ) {
    return <>••••••</>;
  }
  // A choice the template names: shown by the name the form offered it under.
  if (Array.isArray(definition.enum) && Array.isArray(definition.enumNames)) {
    const named = definition.enumNames[definition.enum.indexOf(value)];
    if (named !== undefined) return <>{String(named)}</>;
  }
  if (typeof value === 'string' && ui['ui:field'] === 'RepoUrlPicker') {
    const { host, owner, repo } = repoLocationOf(value);
    const where = [host, owner, repo].filter(Boolean).join('/');
    return <span className={classes.code}>{where}</span>;
  }
  if (typeof value === 'string' && ENTITY_REF.test(value)) {
    return (
      <span className={classes.stack}>
        <EntityDisplayName entityRef={value} hideIcon disableTooltip />
        <span className={classes.mono}>{value}</span>
      </span>
    );
  }
  if (typeof value === 'boolean') return <>{value ? 'Yes' : 'No'}</>;
  if (Array.isArray(value)) return <>{value.join(', ')}</>;
  if (typeof value === 'object') {
    return <span className={classes.code}>{JSON.stringify(value)}</span>;
  }
  return <>{String(value)}</>;
};

type ReviewPhaseProps = FlowProps & {
  steps: ParsedTemplateSchema[];
  labels: string[];
  values: Values;
  createLabel: string;
  backLabel: string;
  onBack?: () => void;
  onStarted: (taskId: string) => void;
};

const ReviewPhase = ({
  steps,
  labels,
  values,
  template,
  templateRef,
  title,
  titleId,
  createLabel,
  backLabel,
  onClose,
  onBack,
  onStarted,
}: ReviewPhaseProps) => {
  const classes = usePanelStyles();
  const review = useReviewStyles();
  const scaffolderApi = useApi(scaffolderApiRef);
  const { secrets } = useTemplateSecrets();
  const [starting, setStarting] = useState(false);
  const [refused, setRefused] = useState<string>();
  const happens = stepNamesOf(template);
  const rows = reviewRows(steps, values);

  const create = async () => {
    setStarting(true);
    setRefused(undefined);
    try {
      const { taskId } = await scaffolderApi.scaffold({
        templateRef,
        values,
        secrets,
      });
      onStarted(taskId);
    } catch (error) {
      setRefused(error instanceof Error ? error.message : String(error));
      setStarting(false);
    }
  };

  return (
    <PanelShell
      titleId={titleId}
      title={title}
      onClose={onClose}
      status={<StepIndicator labels={labels} current={labels.length - 1} />}
      foot={
        <>
          <Button
            variant="outlined"
            onClick={onBack ?? onClose}
            disabled={starting}
          >
            {onBack ? backLabel : 'Cancel'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={create}
            disabled={starting}
            startIcon={<CheckIcon />}
          >
            {starting ? 'Starting…' : createLabel}
          </Button>
        </>
      }
    >
      <dl className={review.values} aria-label="What you entered">
        {rows.map(row => (
          <Fragment key={row.key}>
            <dt>{row.label}</dt>
            <dd>
              <ReviewValue
                value={values[row.key]}
                ui={row.ui}
                definition={row.definition}
              />
            </dd>
          </Fragment>
        ))}
      </dl>
      {happens.length > 0 && (
        <>
          <hr className={review.rule} />
          <section aria-labelledby={`${titleId}-happens`}>
            <h3 id={`${titleId}-happens`} className={review.heading}>
              What happens when you create it
            </h3>
            <ol className={review.happens}>
              {happens.map((name, index) => (
                <li key={`${index}-${name}`}>
                  <span className={review.number} aria-hidden>
                    {index + 1}
                  </span>
                  {name}
                </li>
              ))}
            </ol>
          </section>
        </>
      )}
      {refused && (
        <Typography role="alert" className={classes.alert}>
          The run could not be started: {refused.replace(/\.$/, '')}. Nothing
          was created, and everything you entered is still here.
        </Typography>
      )}
    </PanelShell>
  );
};

const Flow = (props: FlowProps) => {
  const { manifest, title, titleId, onClose, onRunFinished } = props;
  const { steps, presentation } = useTemplateSchema(manifest);
  const [initialState] = useFormDataFromQuery();
  const [values, setValues] = useState<Values>(initialState);
  const first: Phase = steps.length
    ? { kind: 'form', page: 0 }
    : { kind: 'review' };
  const [phase, setPhase] = useState<Phase>(first);
  const labels = [
    ...steps.map(step => step.title),
    presentation?.buttonLabels?.reviewButtonText ?? 'Review',
  ];
  const subject = subjectOf(steps, values) ?? title;

  if (phase.kind === 'run') {
    return (
      <RunPhase
        taskId={phase.taskId}
        title={title}
        titleId={titleId}
        subject={subject}
        onClose={onClose}
        onFinished={onRunFinished}
        onBackToForm={() => setPhase(first)}
        onAnother={() => {
          setValues({});
          setPhase(first);
        }}
      />
    );
  }

  if (phase.kind === 'review') {
    return (
      <ReviewPhase
        {...props}
        steps={steps}
        labels={labels}
        values={values}
        createLabel={presentation?.buttonLabels?.createButtonText ?? 'Create'}
        backLabel={presentation?.buttonLabels?.backButtonText ?? 'Back'}
        onBack={
          steps.length
            ? () => setPhase({ kind: 'form', page: steps.length - 1 })
            : undefined
        }
        onStarted={taskId => setPhase({ kind: 'run', taskId })}
      />
    );
  }

  const page = phase.page;
  return (
    <FormPhase
      key={page}
      {...props}
      step={steps[page]}
      page={page}
      labels={labels}
      values={values}
      setValues={setValues}
      backLabel={presentation?.buttonLabels?.backButtonText ?? 'Back'}
      nextLabel={
        page === steps.length - 1
          ? (presentation?.buttonLabels?.reviewButtonText ?? 'Review')
          : 'Next'
      }
      onBack={
        page > 0 ? () => setPhase({ kind: 'form', page: page - 1 }) : undefined
      }
      onNext={() =>
        setPhase(
          page + 1 < steps.length
            ? { kind: 'form', page: page + 1 }
            : { kind: 'review' },
        )
      }
    />
  );
};

export const TemplatePanel = (props: Props) => {
  const classes = usePanelStyles();
  const titleId = useId();
  const {
    manifest: asked,
    loading,
    error,
  } = useTemplateParameterSchema(props.templateRef);
  // Steps and fields behind a feature flag that is off are left out, as the
  // engine's own form leaves them out.
  const manifest = useFilteredSchemaProperties(asked);
  const title =
    manifest?.title ?? (props.template ? titleOf(props.template) : 'Template');

  return (
    <Drawer
      anchor="right"
      open
      onClose={props.onClose}
      PaperProps={{
        className: classes.paper,
        role: 'dialog',
        'aria-labelledby': titleId,
      }}
    >
      {manifest ? (
        <Flow {...props} manifest={manifest} title={title} titleId={titleId} />
      ) : (
        <PanelShell
          titleId={titleId}
          title={title}
          onClose={props.onClose}
          foot={
            <Button variant="outlined" onClick={props.onClose}>
              Close
            </Button>
          }
        >
          {loading ? (
            <Progress />
          ) : (
            <Typography role="alert" className={classes.alert}>
              This template could not be loaded
              {error?.message ? `: ${error.message}` : '.'}
            </Typography>
          )}
        </PanelShell>
      )}
    </Drawer>
  );
};
